import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, retry, throwError, timeout, timer } from 'rxjs';
import { mapErpError } from './erp-api.errors';
import { ERP_API_URL } from './erp-api.tokens';

export type ErpCompanySelectDto = {
  uuid: string;
  name: string;
};

const REQUEST_TIMEOUT_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class ErpCompaniesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ERP_API_URL).replace(/\/+$/, '')}/companies`;

  activeSelects(): Promise<ErpCompanySelectDto[]> {
    return this.get(`${this.baseUrl}/active-selects`);
  }

  private async get(url: string): Promise<ErpCompanySelectDto[]> {
    try {
      const body = await firstValueFrom(
        this.http.get<unknown>(url).pipe(
          timeout(REQUEST_TIMEOUT_MS),
          retry({ count: 1, delay: error => retryDelay(error) }),
        ),
      );
      return unwrapCompanies(body);
    } catch (error) {
      throw mapErpError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      return timer(seconds * 1000);
    }
    if (error.status >= 500 || error.status === 0) return timer(300);
  }
  return throwError(() => error);
}

function unwrapCompanies(body: unknown): ErpCompanySelectDto[] {
  const data =
    body && typeof body === 'object' && 'data' in body
      ? (body as { data: unknown }).data
      : body;
  if (!Array.isArray(data)) throw mapErpError(new Error('unexpected'));
  return data.map(item => {
    const record = item && typeof item === 'object' ? (item as Record<string, unknown>) : null;
    const uuid = record && typeof record['uuid'] === 'string' ? record['uuid'] : '';
    const name = record && typeof record['name'] === 'string' ? record['name'] : '';
    if (!uuid || !name) throw mapErpError(new Error('unexpected'));
    return { uuid, name };
  });
}
